import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Button, LinkButton } from '../../components/ui/Button';
import { SelectField, TextField } from '../../components/ui/Field';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { StatusTimeline } from '../../components/admin/StatusTimeline';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseStatuses, photoStages } from '../../types/admin';
import type { CaseStatus, PhotoStage } from '../../types/admin';
import { validateImage } from '../../services/imageService';
import { formatDate, formatDateTime, formatPrice, telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function CaseDetail() {
  const {
    caseId
  } = useParams();
  const navigate = useNavigate();
  const {
    cases,
    customerById,
    setStatus,
    addNote,
    deleteNote,
    addPhotos,
    deletePhoto,
    updateCase,
    deleteCase
  } = useAdminData();
  const item = cases.find(entry => entry.id === caseId);
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<PhotoStage>('Før');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  if (!item) return <Navigate to="/admin/sager" replace data-id="element-247" data-mp="2xon5vlqxv5ic" />;
  const customer = customerById(item.customerId);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-248" data-mp="2sqv1vtd0rsxs">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-249" data-mp="37gkj0y1ic7jp">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-250" data-mp="3ggpeqaxc9hi1" />
        Sager
      </Link>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-251" data-mp="3gecx5yvht95i">
        <div data-id="element-252" data-mp="3cgyr2g33zhhq">
          <p className="text-sm text-ink-muted" data-id="element-253" data-mp="3cdavzci0mn0w">Sag #{item.number}</p>
          <h1 className="mt-1 font-display text-[clamp(1.7rem,4.5vw,2.4rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-254" data-mp="3jjvl1lovfymi">
            {item.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-255" data-mp="3tg8ag7ddvqbg">
            {customer?.name ?? item.customerName}
            {item.fromWebsite && <span className="ml-2 rounded-full bg-canvas-deep px-2 py-0.5 text-xs text-ink-muted" data-id="element-256" data-mp="23x6hnb8l13y">
                Fra hjemmesiden
              </span>}
          </p>
        </div>
        <div className="mt-2 flex items-center gap-3" data-id="element-257" data-mp="b0o0zva547wf">
          <StatusBadge status={item.status} data-id="element-258" data-mp="g1bzt34m1t17" />
          <button type="button" onClick={() => {
          if (window.confirm(`Slet sag #${item.number}? Handlingen kan ikke fortrydes.`)) {
            deleteCase(item.id);
            navigate('/admin/sager');
          }
        }} className="inline-flex h-9 items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-259" data-mp="g2df5jsnt1oq">
            <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-260" data-mp="z4nicylxe23k" />
            Slet sag
          </button>
        </div>
      </header>

      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-261" data-mp="q5io44hecux7">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-262" data-mp="q3fqozdwzzig">
          <SelectField id="sag-status" label="Status" className="w-full sm:w-64" value={item.status} onChange={event => setStatus(item.id, event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
          value: option,
          label: option
        }))} data-id="element-263" data-mp="1mv9hu1hv6e1y" />
        </div>
        <div className="mt-6" data-id="element-264" data-mp="2foctrcq7ef2">
          <StatusTimeline status={item.status} data-id="element-265" data-mp="2gzdhiavk61y" />
        </div>
      </section>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-266" data-mp="3tlppu5g94i3c">
        <div className="space-y-5" data-id="element-267" data-mp="3tolq40fkniuy">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-268" data-mp="1ldavqciiy9ua">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-269" data-mp="1q410pon1gaog">Opgave</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-270" data-mp="1ccuwxgcq7qvr">
              {item.description || 'Ingen beskrivelse endnu.'}
            </p>
            <dl className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-3" data-id="element-271" data-mp="1hch87k8zux1x">
              <div data-id="element-272" data-mp="252l8bhmz4eo6">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-273" data-mp="252bjvyhkufkl">Kategori</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-274" data-mp="292lq9c9a77zz">{item.category}</dd>
              </div>
              <div data-id="element-275" data-mp="1v2uezja5g5po">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-276" data-mp="1v5gkzehe6d5s">Adresse</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-277" data-mp="200m98jy9o3x1">{item.address || '—'}</dd>
              </div>
              <div data-id="element-278" data-mp="jqa4sdoffar5">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-279" data-mp="ona9ol9ji7go">Planlagt dato</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-280" data-mp="h3rab4fzo1lx">{formatDate(item.plannedDate)}</dd>
              </div>
            </dl>
            <TextField id="sag-dato" label="Ret planlagt dato" type="date" className="mt-5 max-w-xs" value={item.plannedDate} onChange={event => updateCase(item.id, {
            plannedDate: event.target.value
          })} data-id="element-281" data-mp="c5hd6oapeqxo" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-282" data-mp="3tlzb87tn0kwd">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-283" data-mp="3pkdq5z6y90b3">Noter</h2>
            <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={event => {
            event.preventDefault();
            if (!note.trim()) return;
            addNote(item.id, note.trim());
            setNote('');
          }} data-id="element-284" data-mp="764orz46pbsz">
              <label htmlFor="ny-note" className="sr-only" data-id="element-285" data-mp="35wdbnj9cs3nu">
                Ny note
              </label>
              <input id="ny-note" value={note} onChange={event => setNote(event.target.value)} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." className="flex-1 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-faint transition-colors duration-200 ease-smooth focus:border-accent focus:outline-none" data-id="element-286" data-mp="19rfcekzw8x48" />
              <Button type="submit" variant="dark" data-id="element-287" data-mp="qvxo2rvwra2l">
                Tilføj note
              </Button>
            </form>
            <ul className="mt-5 space-y-3" data-id="element-288" data-mp="10s0mbmnn2ok9">
              {item.internalNotes.map(entry => <motion.li key={entry.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              ease
            }} className="flex items-start justify-between gap-3 rounded-xl bg-canvas p-4" data-id="element-289" data-mp="14t44ift5swon">
                  <div data-id="element-290" data-mp="4k4b5wwcb1vk">
                    <p className="text-xs text-ink-muted" data-id="element-291" data-mp="4knrxaa97k80">{formatDateTime(entry.createdAt)}</p>
                    <p className="mt-1 text-[15px] leading-relaxed text-ink" data-id="element-292" data-mp="k3xwfkz8wq3">{entry.text}</p>
                  </div>
                  <button type="button" onClick={() => deleteNote(item.id, entry.id)} aria-label="Slet note" className="shrink-0 rounded-full p-1.5 text-ink-faint transition-colors duration-200 ease-smooth hover:bg-white hover:text-ink" data-id="element-293" data-mp="3rw5d1fdtfkth">
                    <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-294" data-mp="38yvr9rl0pv92" />
                  </button>
                </motion.li>)}
              {item.internalNotes.length === 0 && <li className="text-sm text-ink-muted" data-id="element-295" data-mp="177r53b1utmw4">Ingen noter endnu.</li>}
            </ul>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-296" data-mp="129z842mcral2">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-297" data-mp="b324k0flo8lx">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-298" data-mp="g2op6uxrcr4r">Billeder</h2>
              <div className="flex items-center gap-2" data-id="element-299" data-mp="1ds8k9hmy5sj">
                <div className="flex gap-1.5" role="group" aria-label="Vælg kategori for upload" data-id="element-300" data-mp="1d92pr59f7uv">
                  {photoStages.map(option => <button key={option} type="button" onClick={() => setStage(option)} aria-pressed={stage === option} className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ease-smooth ${stage === option ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-301" data-mp="3oiq827ql5pir">
                      {option}
                    </button>)}
                </div>
                <label htmlFor="sag-billeder" className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink transition-colors duration-200 ease-smooth hover:border-ink/30" data-id="element-302" data-mp="32acggxbabmll">
                  {uploading ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-303" data-mp="377b7h6znjdfk" /> : <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-304" data-mp="3g1hkscpll329" />}
                  {uploading ? 'Uploader …' : 'Upload'}
                  <input id="sag-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={async event => {
                  const input = event.target;
                  const files = Array.from(input.files ?? []);
                  const problems = files.map(validateImage).filter(Boolean) as string[];
                  const accepted = files.filter(file => !validateImage(file));
                  input.value = '';
                  setUploadError(problems.join(' '));
                  if (accepted.length === 0) return;
                  setUploading(true);
                  const result = await addPhotos(item.id, accepted, stage);
                  setUploading(false);
                  if (!result.ok) setUploadError(result.error ?? 'Billederne kunne ikke uploades.');
                }} data-id="element-305" data-mp="3uxfeloc1tvbb" />
                </label>
              </div>
            </div>

            {uploadError && <p role="alert" className="mt-4 text-sm font-medium text-danger" data-id="element-306" data-mp="2q0690hdf2z9d">
                {uploadError}
              </p>}

            <div className="mt-5 space-y-6" data-id="element-307" data-mp="3ni6ca48cax6p">
              {photoStages.map(group => {
              const photos = item.photos.filter(photo => photo.stage === group);
              return <div key={group} data-id="element-308" data-mp="3r0lxd2zhrf2c">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-309" data-mp="3uz2v4vx0vd73">{group}</h3>
                    {photos.length > 0 ? <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-310" data-mp="3vwacvir4yms">
                        {photos.map(photo => <li key={photo.id} className="relative" data-id="element-311" data-mp="ds93nsadxah0">
                            <img src={photo.url} alt={`${group} — ${photo.name}`} className="aspect-square w-full rounded-xl object-cover" data-id="element-312" data-mp="dpwlpf4mn03y" />
                            <button type="button" onClick={() => deletePhoto(item.id, photo.id)} aria-label={`Slet billede ${photo.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-313" data-mp="rht1qkt0tjjz">
                              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-314" data-mp="ljhiadkmr4g8" />
                            </button>
                          </li>)}
                      </ul> : <p className="mt-2 text-sm text-ink-muted" data-id="element-315" data-mp="uimhzn7xh3tz">Ingen billeder.</p>}
                  </div>;
            })}
            </div>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-316" data-mp="14girkal7vqpz">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-317" data-mp="14fhclv12yk2z">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-318" data-mp="zjihlol8y3tl">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-319" data-mp="369gmqd2guve3">
              {customer?.name ?? item.customerName}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-320" data-mp="1grt6l6wdyhw3">
              <li data-id="element-321" data-mp="1lr636jd273bv">{customer?.phone || '—'}</li>
              <li className="break-all" data-id="element-322" data-mp="1lpur7w9gmp9o">{customer?.email || '—'}</li>
              <li data-id="element-323" data-mp="172ruhrsyokne">{customer?.address || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-324" data-mp="170fdphyxwgbx">
              <LinkButton href={telHref(customer?.phone ?? '')} variant="secondary" size="sm" data-id="element-325" data-mp="1c01o7jndvih1">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-326" data-mp="y0sd2ye1bejx" />
                Ring
              </LinkButton>
              <LinkButton href={`mailto:${customer?.email ?? ''}`} variant="secondary" size="sm" data-id="element-327" data-mp="124hw78bxx3xk">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-328" data-mp="o58hkd3duhvp" />
                Send e-mail
              </LinkButton>
            </div>
            {customer && <Link to={`/admin/kunder/${customer.id}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-329" data-mp="2iiggafuhbdmm">
                Se kundeprofil
              </Link>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-330" data-mp="iff9xf64h9cc">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-331" data-mp="idcmn2wctubb">Økonomi</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-332" data-mp="efewnxurf46v">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-333" data-mp="9dzd6a88d5ql">
                <dt className="text-ink-soft" data-id="element-334" data-mp="4fnxvjz6230z">Timepris</dt>
                <dd className="font-medium text-ink" data-id="element-335" data-mp="4h8xho0c8c5p">{formatPrice(item.hourlyRate, 'kr./time')}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-336" data-mp="3quph31ovd4qz">
                <dt className="text-ink-soft" data-id="element-337" data-mp="3qswblppzsqkk">Fast tilbud</dt>
                <dd className="font-medium text-ink" data-id="element-338" data-mp="3muf4v7ahhi3o">{formatPrice(item.fixedPrice)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-339" data-mp="3hszi9asnr3j6">
                <dt className="text-ink-soft" data-id="element-340" data-mp="3cuo7mxujgozw">Anslået beløb</dt>
                <dd className="font-medium text-ink" data-id="element-341" data-mp="3cxamlijre6s5">{formatPrice(item.estimate)}</dd>
              </div>
            </dl>
            <TextField id="sag-slutbeloeb" label="Endeligt beløb (kr.)" className="mt-4" inputMode="numeric" placeholder="Udfyldes når opgaven er færdig" value={item.finalAmount} onChange={event => updateCase(item.id, {
            finalAmount: event.target.value
          })} data-id="element-342" data-mp="1xc01bdkymd6h" />
          </section>
        </aside>
      </div>
    </div>;
}